import { selectUserContext } from 'core/userContext';
import { useSelector } from 'react-redux';
import { ModalContextProvider } from 'utils/contexts/modal';
import { PaginationContextProvider } from 'utils/contexts/pagination';
import { getLabelByMedium } from 'utils/messageMediums';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { doesRbacPermissionHaveAccess, PROJECT_PERMISSIONS } from 'utils/permissions';
import { DOCUMENT_TITLE, PAGE_TITLE } from '../constants';
import { ChannelsContextProvider, SearchContextProvider, useChannelsContext } from '../contexts';
import { ChannelsHeader } from './ChannelsHeader';
import { ChannelsList } from './ChannelsList';
import { Layout, LeftColumn, RightColumn, StyledPageHeader } from './ChannelsPage.styled';
import { ChannelsPagination } from './ChannelsPagination';
import { ChannelsSideNav } from './ChannelsSideNav';
import { ChannelModals } from './modals/ChannelModals';
import { UnauthorizedPage } from './UnauthorizedPage';

const Channels = () => {
  const {
    currentChannels,
    currentPage,
    espAccounts,
    filteredChannels,
    handlePaginationUpdate,
    isLoading,
    itemsPerPage,
    messageMedium,
    messageTypesByChannelId,
    paginatedChannels,
  } = useChannelsContext();
  const currentMessageMedium = getLabelByMedium(messageMedium);
  const sectionTitle = currentMessageMedium
    ? `${currentMessageMedium} ${PAGE_TITLE.toLowerCase()}`
    : PAGE_TITLE;

  const query = useQueryParams();
  // TODO: Remove this when data is available
  const showMockChannelDetails =
    query.get('showMockChannelDetails') && messageMedium === 'whatsApp';

  const paginatedChannelsWhatsAppMock = [
    {
      id: 999,
      createdAt: 1654885016747,
      updatedAt: 1654885016747,
      projectId: 211,
      creatorUserId: 'demo@iterable.com',
      name: 'Marketing Channel',
      channelType: 'Marketing',
      messageMedium: 'WhatsApp',
    },
    {
      id: 998,
      createdAt: 1654885016747,
      updatedAt: 1654885016747,
      projectId: 211,
      creatorUserId: 'demo@iterable.com',
      name: 'Transactional Channel',
      channelType: 'Transactional',
      messageMedium: 'WhatsApp',
    },
  ];

  const messageTypesByIdWhatsAppMock = {
    '999': [
      {
        id: 111,
        createdAt: 1654885016747,
        updatedAt: 1654885016747,
        projectId: 211,
        name: 'Marketing Message',
        messageChannelId: 999,
        subscriptionPolicy: 'OptOut',
      },
      {
        id: 112,
        createdAt: 1658941003510,
        updatedAt: 1658941003510,
        projectId: 211,
        name: 'Untitled message type',
        messageChannelId: 999,
        subscriptionPolicy: 'OptOut',
      },
    ],
    '998': [
      {
        id: 113,
        createdAt: 1654885016747,
        updatedAt: 1654885016747,
        projectId: 211,
        name: 'Transactional Message',
        messageChannelId: 998,
        subscriptionPolicy: 'OptOut',
      },
    ],
  };

  return (
    <>
      <StyledPageHeader documentTitle={DOCUMENT_TITLE}>{PAGE_TITLE}</StyledPageHeader>
      <Layout>
        <LeftColumn>
          <ChannelsSideNav />
        </LeftColumn>
        <RightColumn>
          <ChannelsHeader headingText={sectionTitle} />
          <ChannelsList
            // @ts-ignore
            channels={showMockChannelDetails ? paginatedChannelsWhatsAppMock : currentChannels}
            espAccounts={espAccounts}
            isLoading={isLoading}
            // @ts-ignore
            messageTypesByChannelId={
              showMockChannelDetails ? messageTypesByIdWhatsAppMock : messageTypesByChannelId
            }
            messageMedium={messageMedium}
            // @ts-ignore
            paginatedChannels={
              showMockChannelDetails ? paginatedChannelsWhatsAppMock : paginatedChannels
            }
          />
          {!isLoading && (
            <ChannelsPagination
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              numItems={filteredChannels?.length}
              onUpdate={handlePaginationUpdate}
            />
          )}
        </RightColumn>
      </Layout>
      <ChannelModals />
    </>
  );
};

export const ChannelsPage = () => {
  /** gets access to user permissions */
  const userContext = useSelector(selectUserContext);
  const { rbacPermissions } = userContext;
  const hasPermission = doesRbacPermissionHaveAccess(
    rbacPermissions || [],
    PROJECT_PERMISSIONS.ManageIntegrations,
  );

  if (!hasPermission) {
    return <UnauthorizedPage />;
  }

  return (
    <SearchContextProvider>
      <PaginationContextProvider>
        <ChannelsContextProvider>
          <ModalContextProvider>
            <Channels />
          </ModalContextProvider>
        </ChannelsContextProvider>
      </PaginationContextProvider>
    </SearchContextProvider>
  );
};
